import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, Minus, RefreshCw, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMarketData } from '@/contexts/MarketDataContext';
import { AnimatedCounter } from '@/components/futuristic/AnimatedCounter';
import { EducationalDialog } from './EducationalDialog';
import { useIsMobile } from '@/hooks/use-mobile';

export const LiveRatesCard: React.FC = () => {
  const { marketData, isConnected, connect } = useMarketData();
  const [showDialog, setShowDialog] = useState(false);
  const isMobile = useIsMobile();

  const formatRate = (rate: number) => rate.toFixed(3);
  
  const getInvestorRate = (baseRate: number) => baseRate + 0.75;

  const rates = marketData ? [
    { label: '30-Year Fixed', rate: marketData.interestRates['30_year_fixed'], type: 'conventional' },
    { label: '15-Year Fixed', rate: marketData.interestRates['15_year_fixed'], type: 'conventional' },
    { label: 'FHA 30-Year', rate: marketData.interestRates.fha_30_year || marketData.interestRates['30_year_fixed'], type: 'fha' },
    { label: 'VA 30-Year', rate: marketData.interestRates.va_30_year, type: 'va' },
    { label: '5/1 ARM', rate: marketData.interestRates['5_1_arm'] || marketData.interestRates['30_year_fixed'] - 0.5, type: 'arm' },
  ] : [];

  return (
    <>
      <Card className="h-full bg-card border-border">
        <CardHeader className="pb-2 md:pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-sm md:text-base flex items-center gap-1.5 md:gap-2">
              <TrendingUp className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary flex-shrink-0" />
              <span className="hidden sm:inline">Live Market Rates</span>
              <span className="sm:hidden">Rates</span>
            </CardTitle>
            <div className="flex items-center gap-1.5 md:gap-2">
              <span className={`text-[10px] md:text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full ${isConnected ? 'bg-green-500/10 text-green-600' : 'bg-red-500/10 text-red-600'}`}>
                {isConnected ? '● Live' : '● Off'}
              </span>
              <Button size="sm" variant="ghost" onClick={connect} className="h-5 w-5 md:h-6 md:w-6 p-0">
                <RefreshCw className="w-2.5 h-2.5 md:w-3 md:h-3" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {!marketData ? (
            <div className="text-center py-8 text-muted-foreground">
              <p className="mb-2">Loading market data...</p>
              <Button onClick={connect} variant="outline" size="sm">
                Connect to Live Feed
              </Button>
            </div>
          ) : (
            <>
              <div className="grid gap-1.5 md:gap-2">
                {rates.map((item, idx) => (
                  <div key={idx} className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-1.5 md:p-2 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors gap-1.5 sm:gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-xs md:text-sm font-medium">{item.label}</span>
                    </div>
                    <div className={`flex ${isMobile ? 'justify-start' : 'items-center'} gap-3 md:gap-3`}>
                      <div className="text-left sm:text-right">
                        <div className="font-semibold text-xs md:text-sm">
                          <AnimatedCounter value={item.rate} decimals={3} suffix="%" />
                        </div>
                        <div className="text-[10px] md:text-xs text-muted-foreground">Primary</div>
                      </div>
                      <div className="text-left sm:text-right">
                        <div className="font-semibold text-xs md:text-sm text-orange-600">
                          <AnimatedCounter value={getInvestorRate(item.rate)} decimals={3} suffix="%" />
                        </div>
                        <div className="text-[10px] md:text-xs text-muted-foreground">Investor</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-2 border-t">
                <p className="text-[10px] md:text-xs text-muted-foreground mb-1.5 md:mb-2">
                  Last updated: {marketData.timestamp ? new Date(marketData.timestamp).toLocaleTimeString() : 'N/A'}
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full h-8 text-xs md:text-sm"
                  onClick={() => setShowDialog(true)}
                >
                  <Info className="w-3 h-3 mr-1.5 md:mr-2" />
                  <span className="hidden sm:inline">Learn About Mortgage Rates</span>
                  <span className="sm:hidden">Learn More</span>
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <EducationalDialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        title="Understanding Mortgage Rates"
      >
        <div className="space-y-4">
          <section>
            <h4 className="font-semibold mb-2">What Affects Your Rate?</h4>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• <strong>Credit Score:</strong> Higher scores = lower rates (typically 620+ required)</li>
              <li>• <strong>Down Payment:</strong> 20%+ avoids PMI and gets better rates</li>
              <li>• <strong>Loan Type:</strong> Conventional, FHA, VA, USDA have different rates</li>
              <li>• <strong>Property Type:</strong> Investment properties have higher rates</li>
              <li>• <strong>Loan Term:</strong> Shorter terms (15-year) usually have lower rates</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Primary vs. Investor Rates</h4>
            <p className="text-sm text-muted-foreground mb-2">
              Investment property rates are typically 0.5% to 1.5% higher than primary residence rates because:
            </p>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li>• Higher default risk (borrowers prioritize primary residence)</li>
              <li>• Rental income is less stable than employment income</li>
              <li>• Property may be vacant during sale/refinance</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold mb-2">Rate Lock Strategy</h4>
            <p className="text-sm text-muted-foreground">
              Rates change daily based on market conditions. Most lenders offer rate locks for 30-60 days. 
              Lock your rate when you're satisfied with the offered rate and have at least 30 days until closing.
            </p>
          </section>
        </div>
      </EducationalDialog>
    </>
  );
};
